import { createContext, useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';

interface PosFullscreenContextValue {
  posAppFullscreen: boolean;
  toggleBrowserFS: () => void;
}

export const PosFullscreenContext = createContext<PosFullscreenContextValue>({
  posAppFullscreen: false,
  toggleBrowserFS: () => {},
});

export const usePosFullscreen = () => useContext(PosFullscreenContext);

interface Props {
  children: React.ReactNode;
}

/**
 * App Fullscreen (ẩn sidebar + banner) là HỆ QUẢ của việc đang ở màn POS, KHÔNG
 * phải một công tắc người dùng bật/tắt. Màn POS chỉ hỗ trợ hai chế độ: App
 * Fullscreen, và App + Browser Fullscreen.
 *
 * Trước đây đây là `useState` cộng một handler `fullscreenchange` tắt App
 * Fullscreen mỗi khi thoát Browser Fullscreen. Cơ chế đó không sống được cùng
 * yêu cầu mới: thu ngân thoát Browser Fullscreen giữa ca bán hàng sẽ rơi khỏi
 * App Fullscreen, mà nút bật lại đã bị bỏ — sidebar hiện ra và chỉ reload trang
 * mới về được.
 *
 * Suy từ route thì cả ba việc tự thoả: vào `/pos` là bật, rời `/pos` là tắt
 * (nên các trang khác luôn có sidebar), và thoát Browser Fullscreen không đụng
 * gì tới App Fullscreen.
 *
 * ĐỪNG thêm `toggleAppFS` trở lại. Không còn nơi gọi nào, và thêm vào là mở lại
 * đúng cái mâu thuẫn trên.
 */
export function PosFullscreenProvider({ children }: Props) {
  const { pathname } = useLocation();
  const posAppFullscreen = pathname.replace(/\/+$/, '') === '/pos';

  const toggleBrowserFS = useCallback(async () => {
    if (!document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
      } catch {
        // Trình duyệt từ chối fullscreen — không có gì để làm thêm.
      }
    } else {
      await document.exitFullscreen().catch(() => {});
    }
  }, []);

  return (
    <PosFullscreenContext.Provider value={{ posAppFullscreen, toggleBrowserFS }}>
      {children}
    </PosFullscreenContext.Provider>
  );
}
